import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map } from 'rxjs';

import { NotesSyncApiService } from '../notes-sync-api.service';
import { LogService } from '../log.service';
import { SyncStateService } from './sync-state.service';

export interface RemoteChangesResult {
  results: any[];
  last_seq: string;
}
export interface EnrichedChange {
  id: string;
  deleted?: boolean;
  doc?: any;
}

@Injectable({ providedIn: 'root' })
export class RemoteChangesService {
  /** Last sequence successfully acknowledged by the client */
  private readonly lastPulledSeq$ = new BehaviorSubject<string>('0');

  /** Last sequence observed on the server */
  private readonly remoteSeq$ = new BehaviorSubject<string>('0');

  /** Public signal: do we need to pull? */
  readonly hasPendingPull$ = combineLatest([
    this.lastPulledSeq$,
    this.remoteSeq$
  ]).pipe(
    map(([lastPulled, remote]) => remote !== lastPulled),
    distinctUntilChanged()
  );

  constructor(
    private readonly api: NotesSyncApiService,
    private readonly logs: LogService,
    private readonly syncState: SyncStateService
  ) {
    const stored = this.syncState.getLastSeq();
    this.lastPulledSeq$.next(stored);
  }


  getLastPulledSeq(): string {
  return this.lastPulledSeq$.value;
}

  /**
   * One-shot check for remote changes.
   * NO decisions here – coordinator decides what to do.
   */
async checkOnce(since: string): Promise<RemoteChangesResult> {
  //this.logs.append('synch', '[DEBUG] RemoteChangesService.checkOnce ENTERED');

  const { results, last_seq } = await this.api.getChanges(since);

  if (!results.length) {
    return { results: [], last_seq };
  }

  // Extract ids + revs
  const docsToFetch = results
    .filter((c: any) => !c.deleted)
    .map((c: any) => ({
      id: c.id,
      rev: c.changes?.[0]?.rev
    }));

  const fetchedDocs = await this.api.bulkGetNotes(docsToFetch);

  const docMap = new Map(
    fetchedDocs.map((d: any) => [d._id, d])
  );

  const enriched: EnrichedChange[] = results.map((c: any) => ({
    id: c.id,
    deleted: c.deleted,
    doc: c.deleted ? undefined : docMap.get(c.id)
  }));

  this.remoteSeq$.next(last_seq);

  return {
    results: enriched,
    last_seq
  };
}

  /**
   * Acknowledge that pull has completed successfully.
   * (Called by the pull executor / coordinator.)
   */
  markPulled(lastSeq: string): void {
    this.lastPulledSeq$.next(lastSeq);
    this.syncState.setLastSeq(lastSeq);
    // this.logs.append(
    //   'synch',
    //   `[REMOTE] pull acknowledged`
    // );
  }

  resetLastSeq(): void {
    this.lastPulledSeq$.next('0');
    this.remoteSeq$.next('0');
    this.syncState.clear();
    this.logs.append('synch', '[REMOTE] last_seq reset to 0');
  }

  /** Optional debug snapshot */
  getDebugState() {
    return {
      lastPulledSeq: this.lastPulledSeq$.value,
      remoteSeq: this.remoteSeq$.value
    };
  }
}
